// Translation dictionary for Norwegian (default) and English.
// To add a new string: add it to BOTH `no` and `en` blocks, using the same key path.
// Then call `t("path.to.key")` from any component via the `useT()` hook.

export type Language = "no" | "en";

// Type-safe nested translation structure. Both languages must mirror each other.
export const translations = {
  no: {
    common: {
      loading: "Laster...",
      save: "Lagre",
      saving: "Lagrer...",
      cancel: "Avbryt",
      delete: "Slett",
      deleting: "Sletter...",
      edit: "Rediger",
      create: "Opprett",
      creating: "Oppretter...",
      add: "Legg til",
      remove: "Fjern",
      close: "Lukk",
      confirm: "Bekreft",
      back: "Tilbake",
      next: "Neste",
      previous: "Forrige",
      submit: "Send inn",
      search: "Søk",
      filter: "Filter",
      all: "Alle",
      yes: "Ja",
      no: "Nei",
      ok: "OK",
      error: "Feil",
      success: "Vellykket",
      warning: "Advarsel",
      info: "Info",
      required: "Påkrevd",
      optional: "Valgfri",
      details: "Detaljer",
      actions: "Handlinger",
      status: "Status",
      name: "Navn",
      email: "E-post",
      phone: "Telefon",
      address: "Adresse",
      date: "Dato",
      from: "Fra",
      to: "Til",
      total: "Total",
      quantity: "Antall",
      price: "Pris",
      description: "Beskrivelse",
      notes: "Notater",
      created: "Opprettet",
      updated: "Oppdatert",
      unknown: "Ukjent",
      none: "Ingen",
      noResults: "Ingen resultater",
      seeAll: "Se alle",
      open: "Åpne",
      view: "Vis",
      download: "Last ned",
      upload: "Last opp",
      print: "Skriv ut",
      copy: "Kopier",
      copied: "Kopiert",
      send: "Send",
      sending: "Sender...",
      retry: "Prøv igjen",
      refresh: "Oppdater",
      selectOption: "Velg ...",
      categories: "Kategorier",
      category: "Kategori",
    },
    nav: {
      sectionAdmin: "Admin",
      sectionWarehouse: "Lager",
      sectionTransport: "Transport",
      sectionHistory: "Historikk",
      sectionSales: "Salg",
      sectionOperations: "Dagens drift",
      sectionFinance: "Økonomi",
      sectionSettings: "Innstillinger",
      dashboard: "Dashbord",
      orders: "Ordrer",
      newOrder: "Ny ordre",
      readyForInvoicing: "Klargjøringskontroll",
      economyInvoices: "Fakturaoversikt",
      economyReceipts: "Kvitteringsstatus",
      vippsLinks: "Vipps-lenker",
      shippingBooking: "Fraktbooking",
      weeklyPlan: "Ukeplan",
      quotes: "Tilbud",
      inquiries: "Forespørsler",
      contracts: "Kontrakter",
      contractTerms: "Kontraktbetingelser",
      customers: "Kunder",
      products: "Produkter",
      services: "Tjenester",
      shippingOptions: "Fraktalternativer",
      standardPackages: "Standardpakker",
      notifications: "Varsler",
      fileArchive: "Filarkiv",
      errorLogs: "Feillogg",
      emailInbox: "E-postinnboks",
      users: "Brukere",
      packing: "Pakking",
      training: "Opplæring",
      myPackedOrders: "Mine pakkede ordrer",
      receiving: "Mottak og kontroll",
      inspection: "Kontrollkø",
      preparation: "Vask og klargjøring",
      myStatistics: "Min statistikk",
      workLog: "Min arbeidslogg",
      stockStatus: "Lagerstatus",
      playbackDevices: "Avspillingsenheter",
      loans: "Utlån",
      warehouseLocations: "Lagerposisjoner",
      calendar: "Tilgjengelighet",
      capacity: "Kapasitet",
      keyFigures: "Nøkkeltall",
      drift: "Drift",
      automation: "Automatikk",
      inventoryCounts: "Varetelling",
      discrepancyLog: "Avvikslogg",
      issues: "Avvik",
      receivingStats: "Mottaksstatistikk",
      userActivity: "Brukeraktivitet",
      shipping: "Sending",
      returnTracking: "Retursporing",
      activityLog: "Aktivitetslogg",
      settings: "Innstillinger",
      logout: "Logg ut",
      administration: "Administrasjon",
    },
    header: {
      themeLight: "Bytt til lys modus",
      themeDark: "Bytt til mørk modus",
      languageNorwegian: "Norsk",
      languageEnglish: "Engelsk",
      changeLanguage: "Bytt språk",
      selectWarehouse: "Velg lager",
      allWarehouses: "Alle lagre",
      loadingWarehouses: "Laster lagre…",
    },
    role: {
      admin: "Admin",
      warehouse: "Lager",
      driver: "Sjåfør",
      tech: "Tekniker",
      user: "Bruker",
    },
    login: {
      title: "Utstyrsutleie",
      subtitle: "Logg inn for å fortsette",
      username: "Brukernavn",
      usernamePlaceholder: "Brukernavn",
      password: "Passord",
      passwordPlaceholder: "Passord",
      loginButton: "Logg inn",
      errorTitle: "Innlogging feilet",
      errorMissingFields: "Fyll inn brukernavn og passord",
      errorInvalidCredentials: "Feil brukernavn eller passord",
    },
    dashboard: {
      title: "Dashbord",
      subtitle: "Oversikt over utstyrsutleie og ordrer",
      newOrder: "Ny ordre",
      statNewInquiries: "Nye forespørsler",
      statPaymentsToSend: "Betalinger å sende",
      statPaymentsInvoices: "Fakturaer: {{count}}",
      statPaymentsVipps: "Vipps: {{count}}",
      statPaymentsEarly: "+{{count}} kan sendes tidlig",
      statPaymentsOverdue: "{{count}} forfalt: arrangementet er over",
      statPendingPacking: "Ventende pakking",
      statReadyForShipping: "Klar for sending",
      statReadyForPickup: "Klar for henting",
      statInTransit: "Ute hos kunde",
      statReturnsToProcess: "På vei tilbake",
      statAwaitingInspection: "Venter på kontroll",
      statOverdueReturns: "Forfalte returer",
      statLateOut: "Forsinket utsending",
      statLateOutSub: "skulle vært sendt, er ikke det",
      statLabelsToBook: "Fraktlapper å booke",
      statLabelsToBookSub: "forsinket eller innen 3 dager",
      statIssues: "Avvik",
      recentOrders: "Siste ordrer",
      seeAll: "Se alle",
      noOrders: "Ingen ordrer ennå",
      createFirstOrder: "Opprett første ordre",
      quickActions: "Hurtighandlinger",
      quickPacking: "Pakking",
      quickPackingDesc: "Marker ordrer som pakket",
      quickShipping: "Sending",
      quickShippingDesc: "Oppdater sporingsinformasjon",
      quickReceiving: "Mottak retur",
      quickReceivingDesc: "Registrer innkommende returer",
      quickInspection: "Kontroll",
      quickInspectionDesc: "Inspiser returnert utstyr",
    },
    orders: {
      title: "Ordrer",
      subtitle: "Administrer alle ordrer og bestillinger",
      newOrder: "Ny ordre",
      searchPlaceholder: "Søk etter ordrenummer, kunde...",
      filterStatus: "Filtrer status",
      allStatuses: "Alle statuser",
      sortBy: "Sorter etter",
      sortDate: "Nyeste først",
      sortStatus: "Status",
      orderNumber: "Ordrenummer",
      customer: "Kunde",
      status: "Status",
      delivery: "Leveringsmåte",
      eventDate: "Arrangementsdato",
      created: "Opprettet",
      readySince: "Klar for henting siden",
      noOrders: "Ingen ordrer funnet",
      noOrdersHintSearch: "Prøv å endre søk eller filter",
      noOrdersHintEmpty: "Opprett din første ordre for å komme i gang",
      createOrder: "Opprett ordre",
      view: "Se detaljer",
      edit: "Rediger",
      reorder: "Bestill på nytt",
      reorderSuccessTitle: "Ny ordre opprettet",
      reorderSuccessDesc: "Ordre {{orderNumber}} er opprettet basert på tidligere bestilling.",
      reorderErrorDesc: "Kunne ikke opprette ny ordre.",
    },
    status: {
      draft: "Utkast",
      created: "Opprettet",
      packed: "Pakket",
      sent: "Sendt",
      delivered: "Hos kunde",
      received: "Mottatt",
      return_initiated: "Retur startet",
      return_sent: "På vei tilbake",
      return_received: "Retur mottatt",
      inspected: "Kontrollert",
      completed: "Fullført",
      cancelled: "Kansellert",
    },
    deliveryMethod: {
      pickup: "Hent selv",
      posten: "Posten",
      bring: "Bring",
      other: "Annet",
    },
    quoteStatus: {
      draft: "Utkast",
      sent: "Sendt",
      confirmed: "Bekreftet",
      expired: "Utløpt",
      cancelled: "Kansellert",
      rejected: "Avvist",
    },
    contractStatus: {
      draft: "Utkast",
      pending_signature: "Venter på signatur",
      signed: "Signert",
      cancelled: "Kansellert",
      expired: "Utløpt",
    },
    pages: {
      customersTitle: "Kunder",
      customersSubtitle: "Administrer kunder og kontaktinformasjon",
      newCustomer: "Ny kunde",
      inviteCustomer: "Inviter kunde",
      productsTitle: "Produkter",
      productsSubtitle: "Administrer produkter og lagervarer",
      newProduct: "Nytt produkt",
      quotesTitle: "Tilbud",
      quotesSubtitle: "Administrer tilbud og forespørsler",
      newQuote: "Nytt tilbud",
      contractsTitle: "Kontrakter",
      contractsSubtitle: "Administrer kundekontrakter",
      newContract: "Ny kontrakt",
      servicesTitle: "Tjenester",
      servicesSubtitle: "Administrer tjenester og tilleggsprodukter",
      newService: "Ny tjeneste",
      settingsTitle: "Innstillinger",
      settingsSubtitle: "Konfigurer system og firmainnstillinger",
      usersTitle: "Brukere",
      usersSubtitle: "Administrer brukere og tilganger",
      newUser: "Ny bruker",
      packingTitle: "Pakking",
      packingSubtitle: "Pakk og marker ordrer for forsendelse",
      receivingTitle: "Mottak og kontroll",
      receivingSubtitle: "Registrer innkommende returer og inspiser utstyr",
      inspectionTitle: "Kontrollkø",
      inspectionSubtitle: "Mottatte returer som venter på kontroll",
      preparationTitle: "Vask og klargjøring",
      preparationSubtitle: "Klargjør utstyr for neste utleie",
      shippingTitle: "Sending",
      shippingSubtitle: "Send ut ordrer og oppdater sporing",
      returnsTitle: "Retursporing",
      returnsSubtitle: "Følg opp returer på vei tilbake",
      stockTitle: "Lagerstatus",
      stockSubtitle: "Oversikt over tilgjengelig utstyr",
      calendarTitle: "Tilgjengelighet",
      calendarSubtitle: "Se tilgjengelighet over tid",
      inventoryTitle: "Varetelling",
      inventorySubtitle: "Tell varer og oppdater lager",
      discrepancyTitle: "Avvikslogg",
      discrepancySubtitle: "Avvik registrert i lager og kontroll",
      receivingStatsTitle: "Mottaksstatistikk",
      receivingStatsSubtitle: "Vasket, ladet og kontrollert utstyr i valgt periode",
      userActivityTitle: "Brukeraktivitet",
      userActivitySubtitle: "Aktivitet per bruker med detaljert aktivitetslogg",
      myStatisticsTitle: "Min statistikk",
      myStatisticsSubtitle: "Personlig oversikt og produktivitet",
      auditLogTitle: "Aktivitetslogg",
      auditLogSubtitle: "Sporing av endringer i systemet",
      notificationsTitle: "Varsler",
      notificationsSubtitle: "Administrer varsler og maler",
      fileArchiveTitle: "Filarkiv",
      fileArchiveSubtitle: "Sentralt arkiv for vedlegg og dokumenter",
      errorLogsTitle: "Feillogg",
      errorLogsSubtitle: "Systemfeil og logghendelser",
      emailInboxTitle: "E-postinnboks",
      emailInboxSubtitle: "Innkommende e-post knyttet til ordrer",
      warehousesTitle: "Lagerposisjoner",
      warehousesSubtitle: "Administrer lagre og lokasjoner",
      shippingOptionsTitle: "Fraktalternativer",
      shippingOptionsSubtitle: "Konfigurer fraktmetoder og priser",
      standardPackagesTitle: "Standardpakker",
      standardPackagesSubtitle: "Forhåndsdefinerte pakker for raskt valg",
      contractTermsTitle: "Kontraktbetingelser",
      contractTermsSubtitle: "Maler for kontraktbetingelser",
    },
    returnNotes: {
      title: "Beskjeder til mottak",
      subtitle: "Les dem før du åpner kassene.",
      added: "{{who}} la til en beskjed på {{order}}",
      markRead: "Jeg har lest dette",
      later: "Senere",
      readHeading: "Lest",
      readBy: "Lest av {{names}}",
      received: "Returen er mottatt",
      onTheWay: "Returen er ikke kommet ennå",
      popupTitle: "Ny beskjed fra {{who}}",
      popupCounter: "{{total}} uleste",
      menuBadge: "{{total}} uleste beskjeder til mottak",
      inlineTitle: "Beskjed til mottak",
    },
    inspectionQueue: {
      title: "Venter på mottakskontroll",
      subtitle: "Mottatt på lager, men ikke kontrollert ennå.",
      receivedOn: "Mottatt {{when}}",
      headsetCount: "{{count}} headset",
      daysWaitingToday: "Mottatt i dag",
      daysWaiting: "{{days}} d ventet",
      daysWaitingOverdue: "{{days}} d over frist",
      menuBadge: "{{total}} har ventet mer enn 3 dager på kontroll",
    },
    notFound: {
      title: "Siden ble ikke funnet",
      description: "Vi finner ikke siden du leter etter.",
      back: "Gå til forsiden",
    },
  },

  en: {
    common: {
      loading: "Loading...",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      delete: "Delete",
      deleting: "Deleting...",
      edit: "Edit",
      create: "Create",
      creating: "Creating...",
      add: "Add",
      remove: "Remove",
      close: "Close",
      confirm: "Confirm",
      back: "Back",
      next: "Next",
      previous: "Previous",
      submit: "Submit",
      search: "Search",
      filter: "Filter",
      all: "All",
      yes: "Yes",
      no: "No",
      ok: "OK",
      error: "Error",
      success: "Success",
      warning: "Warning",
      info: "Info",
      required: "Required",
      optional: "Optional",
      details: "Details",
      actions: "Actions",
      status: "Status",
      name: "Name",
      email: "Email",
      phone: "Phone",
      address: "Address",
      date: "Date",
      from: "From",
      to: "To",
      total: "Total",
      quantity: "Quantity",
      price: "Price",
      description: "Description",
      notes: "Notes",
      created: "Created",
      updated: "Updated",
      unknown: "Unknown",
      none: "None",
      noResults: "No results",
      seeAll: "See all",
      open: "Open",
      view: "View",
      download: "Download",
      upload: "Upload",
      print: "Print",
      copy: "Copy",
      copied: "Copied",
      send: "Send",
      sending: "Sending...",
      retry: "Retry",
      refresh: "Refresh",
      selectOption: "Select ...",
      categories: "Categories",
      category: "Category",
    },
    nav: {
      sectionAdmin: "Admin",
      sectionWarehouse: "Warehouse",
      sectionTransport: "Transport",
      sectionHistory: "History",
      sectionSales: "Sales",
      sectionOperations: "Today's operations",
      sectionFinance: "Finance",
      sectionSettings: "Settings",
      dashboard: "Dashboard",
      orders: "Orders",
      newOrder: "New order",
      readyForInvoicing: "Readiness check",
      economyInvoices: "Invoice overview",
      economyReceipts: "Receipt status",
      vippsLinks: "Vipps links",
      shippingBooking: "Shipping booking",
      weeklyPlan: "Weekly plan",
      quotes: "Quotes",
      inquiries: "Inquiries",
      contracts: "Contracts",
      contractTerms: "Contract terms",
      customers: "Customers",
      products: "Products",
      services: "Services",
      shippingOptions: "Shipping options",
      standardPackages: "Standard packages",
      notifications: "Notifications",
      fileArchive: "File archive",
      errorLogs: "Error log",
      emailInbox: "Email inbox",
      users: "Users",
      packing: "Packing",
      training: "Training",
      myPackedOrders: "My packed orders",
      receiving: "Receiving & inspection",
      inspection: "Inspection queue",
      preparation: "Cleaning & prep",
      myStatistics: "My statistics",
      workLog: "My work log",
      stockStatus: "Stock status",
      playbackDevices: "Playback devices",
      loans: "Loans",
      warehouseLocations: "Warehouse locations",
      calendar: "Availability",
      capacity: "Capacity",
      keyFigures: "Key figures",
      drift: "Operations",
      automation: "Automation",
      inventoryCounts: "Inventory counts",
      discrepancyLog: "Discrepancy log",
      issues: "Issues",
      receivingStats: "Receiving statistics",
      userActivity: "User activity",
      shipping: "Shipping",
      returnTracking: "Return tracking",
      activityLog: "Activity log",
      settings: "Settings",
      logout: "Log out",
      administration: "Administration",
    },
    header: {
      themeLight: "Switch to light mode",
      themeDark: "Switch to dark mode",
      languageNorwegian: "Norwegian",
      languageEnglish: "English",
      changeLanguage: "Change language",
      selectWarehouse: "Select warehouse",
      allWarehouses: "All warehouses",
      loadingWarehouses: "Loading warehouses…",
    },
    role: {
      admin: "Admin",
      warehouse: "Warehouse",
      driver: "Driver",
      tech: "Technician",
      user: "User",
    },
    login: {
      title: "Equipment Rental",
      subtitle: "Sign in to continue",
      username: "Username",
      usernamePlaceholder: "Username",
      password: "Password",
      passwordPlaceholder: "Password",
      loginButton: "Sign in",
      errorTitle: "Sign-in failed",
      errorMissingFields: "Please enter username and password",
      errorInvalidCredentials: "Invalid username or password",
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Overview of equipment rentals and orders",
      newOrder: "New order",
      statNewInquiries: "New inquiries",
      statPaymentsToSend: "Payments to send",
      statPaymentsInvoices: "Invoices: {{count}}",
      statPaymentsVipps: "Vipps: {{count}}",
      statPaymentsEarly: "+{{count}} can be sent early",
      statPaymentsOverdue: "{{count}} overdue: the event has passed",
      statPendingPacking: "Pending packing",
      statReadyForShipping: "Ready for shipping",
      statReadyForPickup: "Ready for pickup",
      statInTransit: "Out with customer",
      statReturnsToProcess: "Return in transit",
      statAwaitingInspection: "Awaiting inspection",
      statOverdueReturns: "Overdue returns",
      statLateOut: "Late dispatch",
      statLateOutSub: "should have shipped, has not",
      statLabelsToBook: "Book shipping labels",
      statLabelsToBookSub: "overdue or within 3 days",
      statIssues: "Issues",
      recentOrders: "Recent orders",
      seeAll: "See all",
      noOrders: "No orders yet",
      createFirstOrder: "Create first order",
      quickActions: "Quick actions",
      quickPacking: "Packing",
      quickPackingDesc: "Mark orders as packed",
      quickShipping: "Shipping",
      quickShippingDesc: "Update tracking information",
      quickReceiving: "Receive return",
      quickReceivingDesc: "Register incoming returns",
      quickInspection: "Inspection",
      quickInspectionDesc: "Inspect returned equipment",
    },
    orders: {
      title: "Orders",
      subtitle: "Manage all orders and bookings",
      newOrder: "New order",
      searchPlaceholder: "Search by order number, customer...",
      filterStatus: "Filter status",
      allStatuses: "All statuses",
      sortBy: "Sort by",
      sortDate: "Newest first",
      sortStatus: "Status",
      orderNumber: "Order number",
      customer: "Customer",
      status: "Status",
      delivery: "Delivery method",
      eventDate: "Event date",
      created: "Created",
      readySince: "Ready for pickup since",
      noOrders: "No orders found",
      noOrdersHintSearch: "Try changing your search or filter",
      noOrdersHintEmpty: "Create your first order to get started",
      createOrder: "Create order",
      view: "View details",
      edit: "Edit",
      reorder: "Reorder",
      reorderSuccessTitle: "New order created",
      reorderSuccessDesc: "Order {{orderNumber}} was created based on the previous order.",
      reorderErrorDesc: "Could not create new order.",
    },
    status: {
      draft: "Draft",
      created: "Created",
      packed: "Packed",
      sent: "Shipped",
      delivered: "Delivered",
      received: "Received",
      return_initiated: "Return initiated",
      return_sent: "Return in transit",
      return_received: "Return received",
      inspected: "Inspected",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    deliveryMethod: {
      pickup: "Pickup",
      posten: "Posten",
      bring: "Bring",
      other: "Other",
    },
    quoteStatus: {
      draft: "Draft",
      sent: "Sent",
      confirmed: "Confirmed",
      expired: "Expired",
      cancelled: "Cancelled",
      rejected: "Rejected",
    },
    contractStatus: {
      draft: "Draft",
      pending_signature: "Awaiting signature",
      signed: "Signed",
      cancelled: "Cancelled",
      expired: "Expired",
    },
    pages: {
      customersTitle: "Customers",
      customersSubtitle: "Manage customers and contact details",
      newCustomer: "New customer",
      inviteCustomer: "Invite customer",
      productsTitle: "Products",
      productsSubtitle: "Manage products and inventory items",
      newProduct: "New product",
      quotesTitle: "Quotes",
      quotesSubtitle: "Manage quotes and inquiries",
      newQuote: "New quote",
      contractsTitle: "Contracts",
      contractsSubtitle: "Manage customer contracts",
      newContract: "New contract",
      servicesTitle: "Services",
      servicesSubtitle: "Manage services and add-on items",
      newService: "New service",
      settingsTitle: "Settings",
      settingsSubtitle: "Configure system and company settings",
      usersTitle: "Users",
      usersSubtitle: "Manage users and access",
      newUser: "New user",
      packingTitle: "Packing",
      packingSubtitle: "Pack and mark orders for shipment",
      receivingTitle: "Receiving & inspection",
      receivingSubtitle: "Register incoming returns and inspect equipment",
      inspectionTitle: "Inspection queue",
      inspectionSubtitle: "Received returns waiting for inspection",
      preparationTitle: "Cleaning & prep",
      preparationSubtitle: "Prepare equipment for the next rental",
      shippingTitle: "Shipping",
      shippingSubtitle: "Send out orders and update tracking",
      returnsTitle: "Return tracking",
      returnsSubtitle: "Follow returns in transit",
      stockTitle: "Stock status",
      stockSubtitle: "Overview of available equipment",
      calendarTitle: "Availability",
      calendarSubtitle: "View availability over time",
      inventoryTitle: "Inventory counts",
      inventorySubtitle: "Count items and update stock",
      discrepancyTitle: "Discrepancy log",
      discrepancySubtitle: "Discrepancies registered in stock and inspection",
      receivingStatsTitle: "Receiving statistics",
      receivingStatsSubtitle: "Equipment washed, charged and inspected in the selected period",
      userActivityTitle: "User activity",
      userActivitySubtitle: "Activity per user with a detailed activity log",
      myStatisticsTitle: "My statistics",
      myStatisticsSubtitle: "Personal overview and productivity",
      auditLogTitle: "Activity log",
      auditLogSubtitle: "Tracking of changes in the system",
      notificationsTitle: "Notifications",
      notificationsSubtitle: "Manage notifications and templates",
      fileArchiveTitle: "File archive",
      fileArchiveSubtitle: "Central archive for attachments and documents",
      errorLogsTitle: "Error log",
      errorLogsSubtitle: "System errors and log events",
      emailInboxTitle: "Email inbox",
      emailInboxSubtitle: "Incoming email linked to orders",
      warehousesTitle: "Warehouse locations",
      warehousesSubtitle: "Manage warehouses and locations",
      shippingOptionsTitle: "Shipping options",
      shippingOptionsSubtitle: "Configure shipping methods and prices",
      standardPackagesTitle: "Standard packages",
      standardPackagesSubtitle: "Predefined packages for quick selection",
      contractTermsTitle: "Contract terms",
      contractTermsSubtitle: "Templates for contract terms",
    },
    returnNotes: {
      title: "Notes for receiving",
      subtitle: "Read them before you open the boxes.",
      added: "{{who}} added a note to {{order}}",
      markRead: "I have read this",
      later: "Later",
      readHeading: "Read",
      readBy: "Read by {{names}}",
      received: "Return has arrived",
      onTheWay: "Return has not arrived yet",
      popupTitle: "New note from {{who}}",
      popupCounter: "{{total}} unread",
      menuBadge: "{{total}} unread notes for receiving",
      inlineTitle: "Note for receiving",
    },
    inspectionQueue: {
      title: "Waiting for inspection",
      subtitle: "Received at the warehouse, but not inspected yet.",
      receivedOn: "Received {{when}}",
      headsetCount: "{{count}} headsets",
      daysWaitingToday: "Received today",
      daysWaiting: "{{days}}d waiting",
      daysWaitingOverdue: "{{days}}d overdue",
      menuBadge: "{{total}} waiting more than 3 days for inspection",
    },
    notFound: {
      title: "Page not found",
      description: "We could not find the page you were looking for.",
      back: "Go to the home page",
    },
  },
} as const;

// Derive type from Norwegian dictionary (source of truth for keys)
export type TranslationDict = typeof translations.no;

// Recursive helper for building dot-paths like "common.save"
type DotJoin<K, P> = K extends string ? (P extends string ? `${K}.${P}` : K) : never;
type Paths<T> = T extends object
  ? {
      [K in keyof T]: T[K] extends object ? DotJoin<K, Paths<T[K]>> : K;
    }[keyof T]
  : never;

export type TranslationKey = Paths<TranslationDict>;
